import React, { useState } from 'react';
import styled from 'styled-components';
import {
  Button, Col, Row, Table,
} from 'react-bootstrap';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import Section from '../../components/Section';
import FloweringArch from '../../components/FloweringArch';
import PdfOverview from './PdfOverview';
import {
  selectCoverageGoal,
  selectCurrentFloweringArch,
  selectWithAdviceFloweringArch,
} from '../../ducks/floweringArch';
import { selectBeesById, selectBeesWithIsSelected } from '../../ducks/bees';
import { selectCoords } from '../../ducks/area';
import {
  selectPresentAccordingToUserIds,
  selectPresentAccordingToUserPlants,
  selectSelectedPlantsDuringAdvice,
} from '../../ducks/plants';
import TableScrollContainer from '../../components/TableScrollContainer';
import { componentsEnum } from '../../utils/types';
import SelectedAreaDisplayer from '../../components/SelectedAreaDisplayer';
import LinkButton from '../../components/LinkButton';
import GeneratingModal from './GeneratingModal';
import { restartSession } from '../../ducks/session';
import ReactGA from "react-ga4";
import {usePostHog} from "posthog-js/react";

const StyledTable = styled(Table)`
  background-color: white;

  thead th {
    background-color: white;
    position: sticky;
    top: 0;
  }
`;

const buildNameTable = (arrayItems, emptyText) => {
  if (!arrayItems || arrayItems.length === 0) {
    return (
      <p>
        {emptyText || 'Niet verstrekt.'}
      </p>
    );
  }

  // eslint-disable-next-line no-prototype-builtins
  const withRedListProp = arrayItems[0].hasOwnProperty('isRedList');

  return (
    <TableScrollContainer>
      <StyledTable striped bordered>
        <thead>
          <tr>
            <th>Nederlandse naam</th>
            <th>Latijnse naam</th>
            {
              withRedListProp && (
                <th>Rode lijst</th>
              )
            }
          </tr>
        </thead>
        <tbody>
          {
          arrayItems.map((item) => (
            <tr key={item.dutchName}>
              <td>{item.dutchName}</td>
              <td>{item.scientificName}</td>
              {
                withRedListProp && (
                  <td>
                    {
                      item.isRedList
                        ? 'Ja'
                        : 'Nee'
                    }
                  </td>
                )
              }
            </tr>
          ))
        }
        </tbody>
      </StyledTable>
    </TableScrollContainer>
  );
};

const generateDate = (withTime = false) => {
  const dateObj = new Date();
  const month = dateObj.getMonth() + 1;
  const day = dateObj.getDate();
  const year = dateObj.getFullYear();
  const minutes = dateObj.getMinutes();
  const hours = dateObj.getHours();

  const date = `${day}-${month}-${year}`;

  return withTime ? `${date} ${hours}-${minutes}` : date;
};

const Overview = (props) => {
  const posthog = usePostHog()

  const history = useHistory();
  const [mapPNG, setMapPNG] = useState(null);
  const [pdfIsGenerated, setPdfIsGenerated] = useState(false);
  const [pdfIsGenerating, setPdfIsGenerating] = useState(false);
  const dispatch = useDispatch();

  // Area info
  const coordsOfArea = useSelector(selectCoords);
  // Bee info
  const beesWithIsSelected = useSelector(selectBeesWithIsSelected);
  const targetBees = beesWithIsSelected.filter((bee) => bee.isSelected);
  const beesById = useSelector(selectBeesById);
  // Plants
  const plantsChosenDuringAdvice = useSelector(selectSelectedPlantsDuringAdvice);
  const plantsInArea = useSelector(selectPresentAccordingToUserPlants);
  const presentAccordingToUserPlantIds = useSelector(selectPresentAccordingToUserIds);
  // Flowering arches
  const currentFloweringArch = useSelector(selectCurrentFloweringArch);
  const withAdviceFloweringArch = useSelector(selectWithAdviceFloweringArch);
  const coverageGoal = useSelector(selectCoverageGoal);

  const plantsChosenDuringAdviceFilteredExisting = plantsChosenDuringAdvice
    .filter((plant) => !presentAccordingToUserPlantIds.includes(plant.id));

  const handleRestart = () => {
    dispatch(restartSession());
  };

  const generatePdfDocument = () => {
    ReactGA.event("report_download");

    posthog?.capture('report_download')

    setPdfIsGenerating(true);

    setTimeout(async () => {
      const blob = await pdf((
        <PdfOverview
          mapPNG={mapPNG}
          plantsInArea={plantsInArea}
          targetBees={targetBees}
          plantsChosenDuringAdviceFilteredExisting={plantsChosenDuringAdviceFilteredExisting}
          currentFloweringArch={currentFloweringArch}
          withAdviceFloweringArch={withAdviceFloweringArch}
          coverageGoal={coverageGoal}
          beesById={beesById}
          date={generateDate()}
        />
      )).toBlob();
      saveAs(blob, `bloeibogen-overzicht ${generateDate(true)}.pdf`);

      setPdfIsGenerated(true);
      setPdfIsGenerating(false);
    }, 100);
  };

  return (
    <Section
      noCentering
      previousNav={{
        disabled: false,
        handleClick: () => history.push(
          componentsEnum.properties[componentsEnum.plantAdvice].route,
        ),
      }}
      {...props}
    >
      <GeneratingModal
        show={pdfIsGenerating}
      />
      <Row className="justify-content-md-center">
        <Col className="text-center" md={10}>
          <h2>Overzicht</h2>
        </Col>
      </Row>
      <Row>
        <Col>
          <h5>Geselecteerd gebied</h5>
        </Col>
      </Row>

      <Row>
        <Col>
          <SelectedAreaDisplayer getPNG={(png) => setMapPNG(png)} coords={coordsOfArea} />
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <h5>
            Bestaande beplanting zoals aangegeven (
            {plantsInArea.length}
            )
          </h5>
        </Col>
      </Row>
      <Row>
        <Col>
          { buildNameTable(plantsInArea, 'In het geselecteerde gebied is reeds nog geen beplanting aanwezig.')}
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <h5>
            Gekozen doelsoorten bijen (
            {targetBees.length}
            )
          </h5>
        </Col>
      </Row>
      <Row>
        <Col>
          { buildNameTable(targetBees)}
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <h5>Bloeiboog huidige situatie</h5>
          <FloweringArch archInfo={currentFloweringArch} />
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <h5>
            Planten gekozen gedurend advies (
            {plantsChosenDuringAdviceFilteredExisting.length}
            )
          </h5>
        </Col>
      </Row>
      <Row>
        <Col>
          { buildNameTable(plantsChosenDuringAdviceFilteredExisting, 'Geen beplanting gekozen gedurende het advies.')}
        </Col>
      </Row>

      <Row className="mt-5">
        <Col>
          <h5>Bloeiboog nieuwe situatie</h5>
          <FloweringArch archInfo={withAdviceFloweringArch} />
        </Col>
      </Row>

      <Row className="mt-5 mb-5">
        <Col>
          <LinkButton
            disabled={pdfIsGenerating || pdfIsGenerated}
            onClick={generatePdfDocument}
          >
            Download dit overzicht als PDF
          </LinkButton>
        </Col>
      </Row>
      <Row className="mt-5 mb-5">
        <Col>
          <Button
            onClick={handleRestart}
            variant="outline-primary"
          >
            Opnieuw beginnen
          </Button>
        </Col>
      </Row>
    </Section>
  );
};

export default Overview;
